// ============================================================================
// ClientDashboardPage — route /me (guarded). The fancy, cohesive, mobile-first
// portal home (Wave 4). A single delightful surface, fed primarily by ONE call
// (GET /api/client/me/home) plus the existing /me/dashboard for the headline
// KPI tiles, composed as:
//   1. Greeting hero (name + a motivational line) above the Today rings.
//   2. The Today hero — animated kcal/protein/carbs/fat/water rings (FE1).
//   3. Quick actions row — Log meal / Log water (+250 ml inline) / Log exercise.
//   4. This week's coach goal progress (Wave 3) with an entrance animation.
//   5. Next session card + a "homework to do" chip.
//   6. Recent activity feed (meals / exercise / water), animated list-in.
//   7. Mini weight trend sparkline.
//   8. The headline KPI tiles, goals, calendar-sync card, report cross-link.
// Strong visual hierarchy, generous spacing, smooth Framer Motion stagger — ALL
// guarded by prefers-reduced-motion (useReducedMotion) so it degrades cleanly.
// Pulse system untouched; EN + 繁中 via i18n keys only. Own data only.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Banner, Button, Card, CardHeader, EmptyState, StatTile } from "../../components";
import { useClientAuth } from "../auth/ClientAuthContext";
import { useClientDashboard, useClientHome } from "../hooks";
import { ClientCalendarSync } from "./ClientCalendarSync";
import { ClientHomeworkPanel } from "./ClientHomeworkPanel";
import { NextSessionCard } from "./NextSessionCard";
import { QuickActions } from "./QuickActions";
import { RecentActivityFeed } from "./RecentActivityFeed";
import { TodayHero } from "./TodayHero";
import { WeeklyGoalProgress } from "./WeeklyGoalProgress";
import { PackageCard } from "./PackageCard";
import { WeightSparkline } from "./WeightSparkline";
import { HOMEWORK_ANCHOR_ID } from "./dashboardAnchors";
import { fmtNum, resultValue } from "./portalLib";
import { CLIENT_REPORT_PATH } from "../routes";
import type { ClientHome } from "../../api";
import type { Goal } from "../../api";

export function ClientDashboardPage() {
  const { t } = useTranslation("clientPortal");
  const reduce = useReducedMotion();
  const { client } = useClientAuth();
  const dash = useClientDashboard();
  const home = useClientHome();

  const firstName = (client?.name ?? "").trim().split(/\s+/)[0] || client?.name || "";

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay: reduce ? 0 : delay, ease: [0.22, 0.61, 0.36, 1] as const },
  });

  if (dash.isLoading) {
    return <PortalSkeleton />;
  }
  if (dash.isError || !dash.data) {
    return (
      <EmptyState
        title={t("common.loadError")}
        description={t("common.loadErrorBlurb")}
        action={
          <Button size="sm" onClick={() => void dash.refetch()}>
            {t("common.retry")}
          </Button>
        }
      />
    );
  }

  const d = dash.data;
  const m = d.metrics;
  const weight =
    resultValue(m.bmi) !== null || d.latest_measurement?.weight_kg != null
      ? d.latest_measurement?.weight_kg ?? null
      : null;
  const bodyFat = resultValue(m.body_fat);
  const bmi = resultValue(m.bmi);

  return (
    <div className="space-y-7">
      {/* 1 — Greeting hero: name + a motivational line, with a subtle gradient
          wash so the home opens with warmth and personality (Pulse coral/violet
          tints, never restyling a locked primitive). */}
      <motion.div {...fade(0)}>
        <div className="relative overflow-hidden rounded-2xl bg-grad-coral px-6 py-7 text-white shadow-volt">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-white/15 blur-2xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-16 -left-8 h-40 w-40 rounded-full bg-white/10 blur-2xl"
          />
          <div className="relative">
            <p className="font-display text-xs font-bold uppercase tracking-kicker text-white/80">
              {t("dashboard.subtitle")}
            </p>
            <h1 className="mt-1 font-display text-3xl font-extrabold leading-tight tracking-tightest">
              {t("dashboard.greeting", { name: firstName })}
            </h1>
            <p className="mt-1.5 max-w-md text-sm font-medium text-white/90">
              {t("hero.motivation")}
            </p>
          </div>
        </div>
      </motion.div>

      {/* 2 — TODAY hero: the animated kcal/protein/carbs/fat/water rings. The
          dashboard centrepiece, fed by /me/home, with its own loading/error. */}
      <motion.div {...fade(0.04)}>
        <TodayHeroSection home={home} />
      </motion.div>

      {/* 3 — Quick actions: big tappable Log meal / Log water (+250 ml inline) /
          Log exercise. (Message lands in Wave 5.) */}
      <motion.section {...fade(0.08)} aria-label={t("quick.title")} className="space-y-2.5">
        <p className="px-1 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("quick.title")}
        </p>
        <QuickActions />
      </motion.section>

      {/* 4 — This week's coach goal (Wave 3) with a tasteful entrance. */}
      <motion.div {...fade(0.12)}>
        <WeeklyGoalProgress />
      </motion.div>

      {/* Training package progress (counts + expiry; no money). Hidden when no
          active package — PackageCard returns null when data.ok is false. */}
      <motion.div {...fade(0.14)}>
        <PackageCard />
      </motion.div>

      {/* 5 — Next PT session + a "homework to do" chip. */}
      <motion.div {...fade(0.16)}>
        <NextSessionCard
          session={home.data?.next_session ?? null}
          pendingHomework={home.data?.pending_homework_count ?? 0}
        />
      </motion.div>

      {/* 6 — Recent activity feed (meals / exercise / water), animated list-in. */}
      <motion.div {...fade(0.2)}>
        <RecentActivityFeedSection home={home} />
      </motion.div>

      {/* 7 — Mini weight trend sparkline. */}
      <motion.div {...fade(0.24)}>
        <WeightSparkline series={home.data?.trend.weight ?? []} />
      </motion.div>

      {/* — Headline KPI tiles (from /me/dashboard) — at-a-glance numbers. */}
      <motion.div {...fade(0.28)} className="grid grid-cols-2 gap-3">
        {weight != null && (
          <StatTile label={t("dashboard.kpi.weight")} value={weight} decimals={1} suffix=" kg" accent="coral" />
        )}
        {bodyFat != null && (
          <StatTile label={t("dashboard.kpi.bodyFat")} value={bodyFat} decimals={1} suffix="%" accent="violet" />
        )}
        {bmi != null && (
          <StatTile label={t("dashboard.kpi.bmi")} value={bmi} decimals={1} accent="ocean" />
        )}
        <StatTile
          label={t("dashboard.kpi.sessions")}
          value={d.completed_count}
          decimals={0}
          accent="lime"
        />
      </motion.div>

      {/* — Coach homework panel (the chip above scrolls here). */}
      <motion.div {...fade(0.32)} id={HOMEWORK_ANCHOR_ID} className="scroll-mt-20">
        <ClientHomeworkPanel />
      </motion.div>

      {/* — Goals */}
      <motion.div {...fade(0.36)}>
        <Card>
          <CardHeader title={t("dashboard.goals")} />
          {d.goals.length > 0 ? (
            <ul className="space-y-2">
              {d.goals.map((g) => (
                <GoalRow key={g.id} goal={g} />
              ))}
            </ul>
          ) : (
            <p className="rounded-md bg-surface-sunken px-4 py-6 text-center text-sm text-body-soft">
              {t("dashboard.noGoals")}
            </p>
          )}
        </Card>
      </motion.div>

      {/* — Add my training to my calendar — read-only, auto-updating ICS feed. */}
      <motion.div {...fade(0.4)}>
        <ClientCalendarSync />
      </motion.div>

      {/* — Report cross-link */}
      <motion.div {...fade(0.44)}>
        <Card tone="ink" className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-bold text-ondark">
              {t("dashboard.reportSummary")}
            </h3>
            <p className="mt-0.5 text-sm text-ondark-muted">{t("dashboard.viewReport")}</p>
          </div>
          <Link to={CLIENT_REPORT_PATH}>
            <Button variant="secondary" size="sm">{t("dashboard.viewReport")}</Button>
          </Link>
        </Card>
      </motion.div>
    </div>
  );
}

/**
 * The Today hero, with its own loading / error handling around the /me/home
 * aggregate. Kept as a thin wrapper so the rings own their query state without
 * blocking the rest of the dashboard (which loads from /me/dashboard).
 */
function TodayHeroSection({
  home,
}: {
  home: ReturnType<typeof useClientHome>;
}) {
  const { t } = useTranslation("clientPortal");
  const { t: tn } = useTranslation("nutrition");

  if (home.isLoading) {
    return (
      <Card className="space-y-5" aria-hidden>
        <div className="h-6 w-1/3 animate-pulse rounded-md bg-surface-sunken" />
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-center sm:gap-8">
          <div className="h-[172px] w-[172px] shrink-0 animate-pulse rounded-full bg-surface-sunken" />
          <div className="grid w-full grid-cols-2 gap-5 sm:w-auto sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="mx-auto h-24 w-24 animate-pulse rounded-full bg-surface-sunken"
              />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (home.isError || !home.data) {
    return (
      <Card className="space-y-3">
        <CardHeader title={tn("target.title")} />
        <Banner tone="warning" title={tn("target.error")} />
        <Button size="sm" variant="secondary" onClick={() => void home.refetch()}>
          {t("common.retry")}
        </Button>
      </Card>
    );
  }

  return (
    <TodayHero
      nutrition={home.data.today.nutrition}
      water={home.data.today.water}
    />
  );
}

/** Recent-activity feed wrapper — own loading skeleton / quiet error fallback. */
function RecentActivityFeedSection({
  home,
}: {
  home: ReturnType<typeof useClientHome>;
}) {
  const { t } = useTranslation("clientPortal");

  if (home.isLoading) {
    return (
      <Card className="space-y-3" aria-hidden>
        <div className="h-5 w-1/3 animate-pulse rounded-md bg-surface-sunken" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-surface-sunken" />
            <div className="h-9 flex-1 animate-pulse rounded-md bg-surface-sunken" />
          </div>
        ))}
      </Card>
    );
  }

  if (home.isError || !home.data) {
    return (
      <Card className="space-y-3">
        <CardHeader title={t("feed.title")} />
        <Banner tone="warning" title={t("common.loadError")} />
        <Button size="sm" variant="secondary" onClick={() => void home.refetch()}>
          {t("common.retry")}
        </Button>
      </Card>
    );
  }

  const items: ClientHome["recent_activity"] = home.data.recent_activity ?? [];
  return <RecentActivityFeed items={items} />;
}

function GoalRow({ goal }: { goal: Goal }) {
  return (
    <li className="flex items-center justify-between rounded-md bg-surface-sunken px-4 py-3">
      <span className="font-display font-bold text-body">
        {goal.kind || goal.metric_key || "—"}
      </span>
      {goal.target_value != null && (
        <span className="text-sm font-bold text-body-muted">
          → {fmtNum(goal.target_value, 1)}
        </span>
      )}
    </li>
  );
}

function PortalSkeleton() {
  return (
    <div className="space-y-6" aria-hidden>
      <div className="h-28 w-full animate-pulse rounded-2xl bg-surface-sunken" />
      <div className="h-64 w-full animate-pulse rounded-xl bg-surface-sunken" />
      <div className="grid grid-cols-3 gap-2.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-surface-sunken" />
        ))}
      </div>
      <div className="h-52 animate-pulse rounded-xl bg-surface-sunken" />
    </div>
  );
}
